<template>
  <!-- Table Container Card -->
  <b-card no-body>

    <div class="m-2">

      <!-- Table Top -->
      <b-row>
        <!-- Per Page -->
        <b-col
          cols="12"
          md="6"
          class="d-flex align-items-center justify-content-start mb-1 mb-md-0"
        >
          <TableEntries
            v-model="perPage"
            :options="perPageOptions"
          />
          <b-dropdown
            v-if="getUserPermission('EXPORT_ACCOUNT_HISTORY_REPORT')"
            id="dropdown-1"
            v-ripple.400="'rgba(255, 255, 255, 0.15)'"
            :text="$t('Export')"
            variant="primary"
            :disabled="isDisabled"
          >
            <b-dropdown-item @click="csvExport(csvData)">
              {{ $t('CSV') }}
            </b-dropdown-item>
            <b-dropdown-item @click="excelExport(csvData)">
              {{ $t('Excel') }}
            </b-dropdown-item>
          </b-dropdown>
        </b-col>
        <!-- Export -->
        <b-col
          cols="12"
          md="6"
        >
          <div class="d-flex align-items-center justify-content-lg-end">
            <Button
              :label="$t('Filter')"
              size="md"
              icon="FilterIcon"
              show-icon
              class="mr-1"
              @click="showModal('modal-filter')"
            />
            <Button
              :label="$t('Reset Filter')"
              size="md"
              icon="RotateCwIcon"
              show-icon
              @click="resetFilter"
            />
          </div>
        </b-col>
      </b-row>
    </div>

    <b-table
      ref="glTableRef"
      :items="reportData"
      :busy="isBusy"
      :per-page="pageSize"
      :current-page="currentPage"
      responsive
      :fields="translatedTableHeaders"
      primary-key="id"
      show-empty
      :empty-text="$t('No records available')"
      :sort-by.sync="sortBy"
      :sort-desc.sync="sortDesc"
      class="position-relative"
    >

      <template #table-busy>
        <div class="text-center text-danger my-2">
          <b-spinner
            class="align-middle"
            variant="primary"
          />
        </div>
      </template>

      <template #cell(SN)="data">
        {{ getOverallIndex(data.index) }}
      </template>

      <template #cell(financialDate)="data">
        <p class="mb-0">
          {{ data.value ? formatCustomDate(data.value) : '' }}
        </p>
      </template>

      <template #cell(transactionDate)="data">
        <p class="mb-0">
          {{ data.value ? formatDateTime(data.value) : '' }}
        </p>
      </template>

      <!-- Column: account number -->
      <template #cell(accountNumber)="data">
        {{ data.value }}
      </template>

      <!-- Column: Account Name -->
      <template #cell(accountName)="data">
        {{ data.value }}
      </template>

      <template #cell(postedBy)="data">
        <span class="text-nowrap">
          {{ data.value }}
        </span>
      </template>

      <template #cell(postingReferenceNumber)="data">
        <span class="text-nowrap">
          {{ data.value }}
        </span>
      </template>

      <template #cell(debit)="data">
        <span class="text-nowrap">
          {{ data.value ? toBalance(data.value) : '' }}
        </span>
      </template>

      <template #cell(credit)="data">
        <span class="text-nowrap">
          {{ data.value ? toBalance(data.value) : '' }}
        </span>
      </template>

    </b-table>

    <TablePagination
      v-model="currentPage"
      :from="from"
      :to="to"
      :total-rows="totalEntries"
      :per-page="perPage"
    />

    <!-- modal -->
    <b-modal
      id="modal-filter"
      ref="my-modal"
      :ok-title="$t('Apply')"
      :cancel-title="$t('Cancel')"
      cancel-variant="outline-secondary"
      centered
      size="lg"
      no-close-on-backdrop
      :title="$t('Filters')"
      modal-footer
      @ok="handleSubmit"
      @cancel="resetFilters"
    >

      <validation-observer ref="customerAccountHistoryRules">
        <b-form>
          <b-row class="match-height">
            <!-- Account Number -->
            <b-col md="6">
              <InputField
                id="account-number"
                v-model="filter.accountNumber"
                :label="$t('Account Number')"
                :placeholder="$t('Enter Account Number')"
                type="text"
              />
            </b-col>
            <!-- Account Number -->
            <!-- Posting Reference Number -->
            <b-col md="6">
              <InputField
                id="posting-reference-number"
                v-model="filter.postingReferenceNumber"
                :label="$t('Posting Reference Number')"
                :placeholder="$t('Enter Reference Number')"
                type="text"
                for="posting-reference-number"
              />
            </b-col>
            <!-- Posting Reference Number -->
          </b-row>

          <b-row class="match-height">
            <!-- Branch -->
            <b-col md="6">
              <SelectField
                id="branch"
                v-model="filter.branchCode"
                :reduce="option => option.value"
                :label="$t('Branch')"
                :placeholder="$t('Select')"
                :options="branchOptions"
                item="label"
                @input="getBranchValue(filter.branchCode)"
              />
            </b-col>
            <!-- Branch -->
            <!-- Instrument Number -->
            <b-col md="6">
              <InputField
                id="instrument-number"
                v-model="filter.instrumentNumber"
                :label="$t('Instrument Number')"
                :placeholder="$t('Enter Instrument Number')"
                type="text"
                for="instrument-number"
              />
            </b-col>
            <!-- Instrument Number -->
          </b-row>

          <b-row class="match-height">
            <!-- Start Date -->
            <b-col md="6">
              <DatePickerField
                id="financial-date-from"
                v-model="filter.financialDateFrom"
                :min-date="new Date()"
                :label="$t('Financial Date (From)')"
                :error-name="$t('Financial Date From')"
                :config="datePickerConfig"
                :placeholder="$t('Date From')"
              />
            </b-col>
            <!-- Start Date -->
            <!-- End Date -->
            <b-col md="6">
              <DatePickerField
                id="financial-date-to"
                v-model="filter.financialDateTo"
                :min-date="new Date()"
                :label="$t('Financial Date (To)')"
                :error-name="$t('Financial Date To')"
                :config="datePickerConfig"
                :placeholder="$t('Date To')"
              />
            </b-col>
            <!-- End Date -->
          </b-row>
          <b-row class="match-height">
            <!-- Sort Code -->
            <b-col md="6">
              <InputField
                id="entry-code"
                v-model="filter.entryCode"
                :label="$t('Sort Code')"
                :placeholder="$t('Enter Sort Code')"
                type="text"
                for="entry-code"
              />
            </b-col>
            <!-- Sort Code -->
          </b-row>
        </b-form>
      </validation-observer>

      <template
        v-if="isReadonly"
        #modal-footer="{ }"
      >
        <div />
      </template>
    </b-modal>
    <!-- modal -->
  </b-card>

</template>

<script>
import {
  BCard, BRow, BCol, BTable, BSpinner, BForm,
  BModal, BDropdown, BDropdownItem,
} from 'bootstrap-vue'
import { ValidationObserver } from 'vee-validate'
import { required } from '@validations'
import { tableMixins } from '@core/mixins/table'
import { permissionMixin } from '@core/mixins/permission'
import { modalMixins } from '@core/mixins/modal'
import { mapGetters, mapActions } from 'vuex'
import Ripple from 'vue-ripple-directive'
// import jsPDF from 'jspdf'
// import 'jspdf-autotable'
import XLSX from 'xlsx'
import {
  toBalance, toFormatAmount, formatAmount,
} from '@/helpers/formatAmount'
import TablePagination from '@/views/components/table/TablePagination.vue'
import TableEntries from '@/views/components/table/TableEntries.vue'
import InputField from '../../../forms/InputField.vue'
import Button from '../../../components/button/Button.vue'
import SelectField from '../../../forms/SelectField.vue'
import DatePickerField from '../../../forms/DatePickerField.vue'
import toNumber from '../../../../helpers/toNumber'
import { toCustomDateTime, toCustomDate, toDateWithYearFirst } from '../../../../helpers/formatDate'
import { reportDataDetails } from '../../../../helpers/data'
import { reportPermissions } from '../../../../helpers/permissions'

export default {
  components: {
    BCard,
    BRow,
    BCol,
    Button,
    BSpinner,
    BTable,
    TablePagination,
    TableEntries,
    InputField,
    SelectField,
    BModal,
    BDropdown,
    BDropdownItem,
    BForm,
    ValidationObserver,
    DatePickerField,
  },
  directives: {
    Ripple,
  },
  mixins: [modalMixins, tableMixins, permissionMixin],
  data() {
    const now = new Date()
    const today = new Date(now.getFullYear(), now.getMonth(), now.getDate())
    const firstDay = new Date(now.getFullYear(), now.getMonth(), 1)
    const todayDay = new Date(today)
    return {
      toBalance,
      toFormatAmount,
      formatAmount,
      formatToDateWithYears: toDateWithYearFirst,
      firstDate: firstDay,
      todayDate: todayDay,
      query: '',
      statusToggleLabel: '',
      isReadonly: false,
      buttonLabel: this.$t('Save'),
      isEditable: false,
      isDisabled: false,
      isBusy: false,
      required,
      modalTitle: this.$t('Customer Account History Report'),
      sortBy: 'name',
      sortDesc: false,
      tableHeaders: [
        { key: 'SN', label: 'S/N' },
        { key: 'financialDate', sortable: true, label: 'Financial Date' },
        { key: 'transactionDate', sortable: true, label: 'Transaction Date' },
        { key: 'accountNumber', sortable: true, label: 'Account Number' },
        { key: 'accountName', sortable: true, label: 'Account Name' },
        { key: 'postedBy', sortable: true, label: 'Posted By' },
        { key: 'postingReferenceNumber', sortable: true, label: 'Posting Reference Number' },
        { key: 'debit', sortable: true, label: 'Debit' },
        { key: 'credit', sortable: true, label: 'Credit' },
        { key: 'narration', sortable: true, label: 'Narration' },
        { key: 'entryCode', sortable: true, label: 'Sort Code' },
        { key: 'instrumentNumber', sortable: true, label: 'Instrument Number' },
      ],
      filter: {
        accountNumber: '',
        postingReferenceNumber: '',
        financialDateFrom: null,
        financialDateTo: null,
        instrumentNumber: '',
        entryCode: '',
        branchCode: '',
      },
      datePickerConfig: {
        dateFormat: 'Y-m-d',
        maxDate: 'today',
        defaultDate: '',
      },
      reportData: [],
      allSearchedData: [],
      branchRefCode: '',
      selected: '',
      reportInfo: reportDataDetails,
    }
  },
  computed: {
    ...mapGetters({
      branchOptions: 'operations/branchOptions',
      userDetails: 'auth/getCurrentUser',
    }),
    csvData() {
      return this.exportItems.map(item => ({
        ...item,
      }))
    },
    exportItems() {
      return this.allSearchedData.map((item, index) => ({
        'S/N': index + 1,
        'Financial Date': this.formatCustomDate(item.financialDate),
        'Transaction Date': this.formatDateTime(item.transactionDate),
        'Account Number': item.accountNumber,
        'Account Name': item.accountName,
        'Posted By': item.postedBy,
        'Posting Ref. No.': item.postingReferenceNumber,
        Debit: item.debit === '' ? '' : formatAmount(item.debit),
        Credit: item.credit === '' ? '' : formatAmount(item.credit),
        Narration: item.narration,
        'Sort Code': item.entryCode,
        'Instrument Number': item.instrumentNumber,
      }))
    },
    translatedTableHeaders() {
      return this.tableHeaders.map(header => ({ ...header, label: header.label ? this.$t(header.label) : header.key }))
    },
  },
  watch: {
    currentPage(newValue) {
      this.currentPage = newValue
      this.fetchCustomerAccountHistoryReport()
    },
    perPage(newValue) {
      this.perPage = newValue
      this.fetchCustomerAccountHistoryReport()
    },
  },
  mounted() {
    this.fetchCustomerAccountHistoryReport()
  },
  methods: {
    ...mapActions({
      getAccountHistories: 'reports/getTransactionReports',
      getBranchByCode: 'operations/getBranchByCode',
    }),

    resetFilters() {
      this.filter = { ...null }
    },
    resetFilter() {
      this.filter = { ...null }
      this.fetchCustomerAccountHistoryReport()
    },
    async getBranchValue(dataValue) {
      if (dataValue !== null) {
        try {
          const response = await this.getBranchByCode(dataValue)
          const { status, data } = response
          if (status === 200 && data) {
            this.branchRefCode = data
          }
        } catch (error) {
          this.alertError({ text: error })
        } finally {
          this.isBusy = false
        }
      }
    },
    async handleSubmit(bvModalEvt) {
      bvModalEvt.preventDefault()
      this.$refs.customerAccountHistoryRules.validate().then(async success => {
        if (success) {
          this.fetchCustomerAccountHistoryReport()
        }
      })
    },
    async fetchCustomerAccountHistoryReport() {
      try {
        this.hideModal('modal-filter')
        this.isBusy = true
        const response = await this.getAccountHistories({
          search: this.filter,
          page: this.currentPage,
          limit: this.perPage,
          permission: reportPermissions.ACCOUNT_HISTORY_REPORT,
          route: 'customerAccountHistoryReport',
        })
        const { data, status } = response
        if (status === 200 && data.data) {
          this.isBusy = false
          this.reportData = data.data
          this.totalCount = data.recordCount
        } else {
          this.reportData = []
          this.totalCount = 0
        }
      } catch (error) {
        this.alert({ text: error })
      } finally {
        this.isBusy = false
      }
    },
    async csvExport() {
      try {
        this.isBusy = true
        const response = await this.getAccountHistories({
          search: this.filter,
          page: 1,
          limit: 100000,
          permission: reportPermissions.ACCOUNT_HISTORY_REPORT,
          route: 'customerAccountHistoryReport',
        })
        const { status, data } = response
        if (status === 200) {
          this.allSearchedData = data.data
          this.isBusy = false
          this.isDisabled = false
          const arrData = this.exportItems.map(item => ({ ...item }))
          const orgName = this.userDetails.institutionName.toUpperCase()
          let csvContent = 'data:text/csv;charset=utf-8,'
          csvContent += [
            [orgName],
            ['Customer Account History Report'],
            [`${(this.filter.financialDateFrom !== null && this.filter.financialDateTo !== null) ? `Date Range: ${this.formatCustomDate(this.filter.financialDateFrom)} - ${this.formatCustomDate(this.filter.financialDateTo)}` : 'Date Range: All'}`],
            [`${(this.branchRefCode !== '') ? `${this.branchRefCode.name} - ${this.branchRefCode.code}` : 'Branch: All'}`],
            [],

            Object.keys(arrData[0]).join(','),
            ...arrData.map(item => Object.values(item).join(',')),
          ]
            .join('\n')
            .replace(/(^\[)|(\]$)/gm, '')

          const dataValue = encodeURI(csvContent)
          const link = document.createElement('a')
          link.setAttribute('href', dataValue)
          link.setAttribute('download', 'Customer_Accounts_History_Report.csv')
          link.click()
        }
      } catch (error) {
        this.handleError(error)
      } finally {
        this.isBusy = false
      }
    },
    async excelExport() {
      try {
        this.isBusy = true
        const response = await this.getAccountHistories({
          search: this.filter,
          page: 1,
          limit: 100000,
          permission: reportPermissions.ACCOUNT_HISTORY_REPORT,
          route: 'customerAccountHistoryReport',
        })
        const { status, data } = response
        if (status === 200) {
          this.allSearchedData = data.data
          this.isBusy = false
          this.isDisabled = false
          const arrData = this.exportItems.map(item => ({ ...item }))
          const orgName = this.userDetails.institutionName.toUpperCase()
          const filename = 'Customer_Accounts_History_Report.xlsx'
          const headerLists = [
            Object.keys(arrData[0]),
          ]

          const Heading = [
            [orgName],
            ['Customer Account History Report'],
            [`${(this.filter.financialDateFrom !== null && this.filter.financialDateTo !== null) ? `Date Range: ${this.formatCustomDate(this.filter.financialDateFrom)} - ${this.formatCustomDate(this.filter.financialDateTo)}` : 'Date Range: All'}`],
            [`${(this.branchRefCode !== '') ? `${this.branchRefCode.name} - ${this.branchRefCode.code}` : 'Branch: All'}`],
            [],
            headerLists[0], // Headers lists
          ]
          const Data = this.allSearchedData.map((item, index) => ({
            'S/N': index + 1,
            'Financial Date': this.formatCustomDate(item.financialDate),
            'Transaction Date': this.formatDateTime(item.transactionDate),
            'Account Number': item.accountNumber,
            'Account Name': item.accountName,
            'Posted By': item.postedBy,
            'Posting Ref. No.': item.postingReferenceNumber,
            Debit: item.debit === '' ? '' : toFormatAmount(item.debit),
            Credit: item.credit === '' ? '' : toFormatAmount(item.credit),
            Narration: item.narration,
            'Sort Code': item.entryCode,
            'Instrument Number': item.instrumentNumber,
          }))
          const ws = XLSX.utils.aoa_to_sheet(Heading)
          XLSX.utils.sheet_add_json(ws, Data, {
            skipHeader: true,
            origin: -1,
          })
          const wb = XLSX.utils.book_new()
          XLSX.utils.book_append_sheet(wb, ws, 'Accounts History Report')
          XLSX.writeFile(wb, filename)
        }
      } catch (error) {
        this.handleError(error)
      } finally {
        this.isBusy = false
      }
    },

    formatNumber: toNumber,
    formatDateTime: toCustomDateTime,
    formatCustomDate: toCustomDate,
  },
}
</script>

<style lang="scss" scoped>
    .per-page-selector {
        width: 90px;
    }

    .invoice-filter-select {
        min-width: 190px;

        ::v-deep .vs__selected-options{
            flex-wrap: nowrap;
        }

        ::v-deep .vs__selected {
            width: 100px;
        }
    }
</style>

<style lang="scss">
    @import '@core/scss/vue/libs/vue-select.scss';
</style>
